/* Chef Akila – delivery rules (theme settings → "Delivery rules").
   The ONE shared delivery-date engine: announcement bar, product page, homepage and the basket all use it,
   so every date on the site agrees. Works in UK time (GMT/BST) from the shop server's clock
   (the Date header of a /cart.js request), falling back to the device clock until that arrives. */
(() => {
  if (window.CADelivery) return;

  const cfg = window.caDeliveryConfig || {};
  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const MONTHS_SHORT = MONTHS.map((m) => m.slice(0, 3));
  const pad = (n) => String(n).padStart(2, '0');

  // "25-12-2026" lines → Set of "2026-12-25"
  const parseList = (text) =>
    new Set(
      String(text || '')
        .split(/[\n,;]+/)
        .map((line) => line.trim().match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/))
        .filter(Boolean)
        .map((m) => `${m[3]}-${pad(m[2])}-${pad(m[1])}`)
    );

  const blocked = parseList(cfg.blocked);
  const extraSaturdays = parseList(cfg.saturdays);
  const days = cfg.days || [false, false, true, true, true, false, false];
  const cutoff = Number(cfg.cutoff ?? 14);
  const maxDays = Number(cfg.maxDays) || 90;
  const satFee = cfg.satFee || {};
  // Daily order cap: { "2026-11-04": 251, … } kept up to date by the Flow "Count orders per delivery date"
  const dailyLimit = Number(cfg.dailyLimit) || 0;
  const orderCounts = cfg.orderCounts && typeof cfg.orderCounts === 'object' ? cfg.orderCounts : {};
  const isFull = (key) => dailyLimit > 0 && Number(orderCounts[key] || 0) >= dailyLimit;

  /* ---------- clock ---------- */
  let offset = 0; // shop server time minus device time (ms)
  let override = null; // test hook: ?ca_now=2026-10-21T14:05:00%2B01:00 (kept for this browser tab) or CADelivery.setNow()
  try {
    const q = new URLSearchParams(location.search).get('ca_now');
    if (q !== null) {
      if (q) sessionStorage.setItem('caNow', q);
      else sessionStorage.removeItem('caNow');
    }
    const s = sessionStorage.getItem('caNow');
    if (s && !Number.isNaN(Date.parse(s))) override = Date.parse(s);
  } catch (e) { /* storage blocked: no test hook */ }

  const nowMs = () => (override != null ? override : Date.now() + offset);

  // Current date + hour on the UK clock, independent of the shopper's device time zone
  const ukNow = () => {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(new Date(nowMs()))
        .map((p) => [p.type, p.value])
    );
    return { date: new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day)), hour: +parts.hour, minute: +parts.minute };
  };

  /* ---------- dates (UTC-midnight Date objects stand for calendar days) ---------- */
  const iso = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  const addDays = (d, n) => new Date(d.getTime() + n * 86400000);
  const parse = (s) => {
    const m = String(s || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    return iso(d) === `${m[1]}-${m[2]}-${m[3]}` ? d : null;
  };
  const isSaturday = (d) => d.getUTCDay() === 6;

  const isDeliveryDay = (d) => {
    const key = iso(d);
    if (blocked.has(key)) return false;
    if (isFull(key)) return false;
    if (isSaturday(d) && extraSaturdays.has(key)) return true;
    return !!days[d.getUTCDay()];
  };

  // Ordering before the cut-off → delivery from tomorrow; after it → from the day after
  const firstPossible = () => {
    const now = ukNow();
    return addDays(now.date, now.hour < cutoff ? 1 : 2);
  };

  const earliest = () => {
    let d = firstPossible();
    for (let i = 0; i < 120 && !isDeliveryDay(d); i++) d = addDays(d, 1);
    return d;
  };

  const lastDay = () => addDays(earliest(), maxDays);

  // The next n delivery days, starting with the earliest
  const upcoming = (n = 5) => {
    const out = [];
    let d = earliest();
    const end = lastDay();
    while (out.length < n && d <= end) {
      if (isDeliveryDay(d)) out.push(d);
      d = addDays(d, 1);
    }
    return out;
  };

  // Is this stored YYYY-MM-DD still a date we can deliver on?
  const isValidChoice = (s) => {
    const d = parse(s);
    return !!d && d >= earliest() && d <= lastDay() && isDeliveryDay(d);
  };

  const isNextDay = (d) => iso(d) === iso(addDays(ukNow().date, 1));
  const isEarliest = (d) => iso(d) === iso(earliest());
  const hasSaturdayFee = (d) => !!d && isSaturday(d) && !!satFee.enabled && !!satFee.variantId;

  const format = (d, style) => {
    if (typeof d === 'string') d = parse(d);
    if (!d) return '';
    const day = DAY_NAMES[d.getUTCDay()];
    if (style === 'short') return `${day.slice(0, 3)} ${d.getUTCDate()} ${MONTHS_SHORT[d.getUTCMonth()]}`;
    if (style === 'dmy') return `${pad(d.getUTCDate())}-${pad(d.getUTCMonth() + 1)}-${d.getUTCFullYear()}`;
    if (style === 'weekday') return day;
    if (style === 'month') return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
    return `${day} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
  };

  // "2pm" from the cut-off hour
  const cutoffLabel = () => {
    const h = cutoff % 24;
    if (h === 0) return 'midnight';
    if (h === 12) return '12 noon';
    return h < 12 ? `${h}am` : `${h - 12}pm`;
  };

  // "Tuesday to Friday" (or "Tuesday, Thursday and Saturday") from the weekday ticks
  const daysLabel = () => {
    const order = [1, 2, 3, 4, 5, 6, 0];
    const on = order.filter((i) => days[i]);
    if (!on.length) return '';
    const idx = on.map((i) => order.indexOf(i));
    const run = idx.every((v, k) => k === 0 || v === idx[k - 1] + 1);
    if (on.length > 2 && run) return `${DAY_NAMES[on[0]]} to ${DAY_NAMES[on[on.length - 1]]}`;
    const names = on.map((i) => DAY_NAMES[i]);
    return names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  };

  /* ---------- server clock ---------- */
  let syncing = null;
  let lastCart = null;
  const syncServerTime = (force) => {
    if (syncing && !force) return syncing;
    syncing = fetch('/cart.js', { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json', 'X-CA-Cart': '1' } })
      .then((r) => {
        const ms = Date.parse(r.headers.get('Date') || '');
        if (!Number.isNaN(ms)) offset = ms - Date.now();
        return r.ok ? r.json() : null;
      })
      .catch(() => null)
      .then((cart) => {
        if (cart) lastCart = cart;
        render();
        document.dispatchEvent(new CustomEvent('ca:delivery-time', { detail: { cart } }));
        return cart;
      });
    return syncing;
  };

  const render = (root = document) => {
    const e = earliest();
    root.querySelectorAll('[data-ca-delivery-date]').forEach((el) => {
      el.textContent = format(e, el.dataset.caDeliveryDate);
    });
  };

  window.CADelivery = {
    earliest, upcoming, isDeliveryDay, isValidChoice, isSaturday, isNextDay, isEarliest, hasSaturdayFee,
    format, render, iso, parse, addDays, lastDay, ukNow, cutoff, cutoffLabel, daysLabel, syncServerTime,
    satFee, maxDays,
    get lastCart() { return lastCart; },
    // test hook (QA): CADelivery.setNow('2026-10-21T14:05:00+01:00') / CADelivery.setNow(null)
    setNow(v) {
      override = v == null ? null : typeof v === 'number' ? v : Date.parse(v);
      if (Number.isNaN(override)) override = null;
      render();
      document.dispatchEvent(new CustomEvent('ca:delivery-time', { detail: {} }));
    },
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => render());
  else render();
  document.addEventListener('shopify:section:load', (e) => render(e.target));
  // correct the date to the shop's clock once the page has settled (one light request)
  const later = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
  later(() => syncServerTime(), { timeout: 2500 });
})();
